import { useEffect, useState } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import DocumentGallery from '@product-framework/components/DocumentGallery';
import Layout from '@product-framework/components/Layout';
import { IconLibrary, PageSection } from '@product-framework/index';
import {
  Breadcrumb,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useUserAccess,
  LoadingEvent,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import { COMPANY_TYPE } from '@constants/app';

function Help() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const store = useStore();
  const userAccess = useUserAccess();

  const storeName = 'HelpPagesInternal';

  const defaultStateValues = {
    paginationState: {
      pageIndex: 0,
      pageSize: 6,
    },
    filters: [],
    sortingState: [],
  };

  const {
    paginationState,
    filters = [],
    sortingState = [],
  } = store.domain.list.get(storeName, defaultStateValues);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get } = useDataProvider(provider);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'HELP_PAGE',
        defaultMessage: 'Help',
      }),
      icon: IconLibrary.Help,
      onClick: undefined,
    },
  ];

  const getHelpDocuments = async () => {
    const filter = userAccess?.isSP()
      ? `&companyType=$eq:${COMPANY_TYPE.SERVICE_PROVIDER}`
      : '';
    const response = await get(
      `${getApiUrl()}/sxcl/v1/help-document?status=$eq:ACTIVE&limit=${
        paginationState?.pageSize
      }&page=${paginationState?.pageIndex || 1}${filter}`,
    );
    if (response.ok) {
      return response;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const initialize = async () => {
    setLoading(true);
    const response = await getHelpDocuments();
    if (response) {
      setTotalItems(response?.data?.total);
      setDocuments(response?.data?.data);
    } else {
      setDocuments(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, [
    JSON.stringify(paginationState),
    JSON.stringify(filters),
    JSON.stringify(sortingState),
  ]);

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          &nbsp;
        </Grid2>
      </Grid2>
      <PageSection
        containerProps={{ sx: { mb: 3 } }}
        label={formatMessage({
          id: 'HELP_HEADING',
          defaultMessage: 'Help & Training',
        })}>
        <Box sx={{ mt: 4 }}>
          {loading ? (
            <LoadingEvent>Fetching help documents...</LoadingEvent>
          ) : (
            <DocumentGallery
              showPagination
              itemsPerRow={3}
              onCardClick={document => {
                navigate(`/help/view/${document?.slugUrl}`);
              }}
              totalItems={totalItems}
              paginationState={paginationState}
              data={documents}
              onPaginationChange={newPaginationState => {
                store.domain.list.set(storeName, {
                  paginationState: newPaginationState,
                  sortingState,
                  filters,
                });
              }}
            />
          )}
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(Help);
